import { APP_URL, CHABAD_APP_URL, ORGANIZATION_LOGO_DIR, SCREEN_IMAGES_DIR, STORAGE_URL } from '@/utils/const';

// The tints a name's avatar can take — the same set, in the same order, the
// frontend's list pages cycle through, so a name gets the same colour in both.
const AVATAR_COLOR_CLASSES = [
  'row-avatar--violet',
  'row-avatar--green',
  'dn-avatar--amber',
  'dn-avatar--rose',
  'row-avatar--blue',
  'row-avatar--orange',
];

const ROLE_LABELS: Record<string, string> = {
  'super-admin': 'Super Admin',
  owner: 'Owner',
  admin: 'Admin',
  mechanic: 'Mechanic',
  user: 'User',
};

interface AccountLike {
  fname?: string | null;
  lname?: string | null;
  email?: string | null;
}

/** Two-letter initials for an avatar ("Beth Israel" → "BI", "Chabad" → "CH"). */
export function initialsOf(name?: string | null): string {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '–';
  const first = parts[0][0] || '';
  const second = parts.length > 1 ? parts[1][0] : parts[0][1] || '';
  return `${first}${second}`.toUpperCase();
}

/** A stable avatar tint for a name, so a record keeps its colour across renders. */
export function avatarColorClass(name?: string | null): string {
  const str = String(name || '');
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash + str.charCodeAt(i) * (i + 1)) % 997;
  }
  return AVATAR_COLOR_CLASSES[hash % AVATAR_COLOR_CLASSES.length];
}

/** "super-admin" → "Super Admin"; unknown roles are title-cased. */
export function formatRole(role?: string | null): string {
  if (!role) return '—';
  if (ROLE_LABELS[role]) return ROLE_LABELS[role];
  return role
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function accountDisplayName(account?: AccountLike | null): string {
  const a: AccountLike = account || {};
  const full = [a.fname, a.lname].filter(Boolean).join(' ').trim();
  return full || a.email || 'Account';
}

export function accountInitials(account?: AccountLike | null): string {
  const a: AccountLike = account || {};
  const f = (a.fname || '').trim();
  const l = (a.lname || '').trim();
  if (f || l) return ((f[0] || '') + (l[0] || '')).toUpperCase();
  const e = (a.email || '').trim();
  return e ? e.slice(0, 2).toUpperCase() : '··';
}

/** Records written before the storage move keep an absolute URL. */
function storageUrl(dir: string, file?: string | null): string {
  if (!file) return '';
  return /^https?:\/\//i.test(file) ? file : `${STORAGE_URL}/${dir}/${file}`;
}

export function organizationLogoUrl(logo?: string | null): string {
  return storageUrl(ORGANIZATION_LOGO_DIR, logo);
}

export function screenImageUrl(image?: string | null): string {
  return storageUrl(SCREEN_IMAGES_DIR, image);
}

/**
 * Where a screen plays: its page on one of the apps its organization uses
 * (Chabad Digital or Screenkode — Screenkode when none is given), never the
 * admin.
 */
export function screenDisplayUrl(screenId: string, app?: string | null): string {
  const base = app === 'chabad_digital' ? CHABAD_APP_URL : APP_URL;
  return `${base}/display/${encodeURIComponent(screenId)}`;
}

const RELATIVE_UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 365 * 24 * 60 * 60],
  ['month', 30 * 24 * 60 * 60],
  ['week', 7 * 24 * 60 * 60],
  ['day', 24 * 60 * 60],
  ['hour', 60 * 60],
  ['minute', 60],
];

const relativeFormatter = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

/** "3 hours ago", "yesterday", "just now" — for the subtext under a date. */
export function timeAgo(value?: string | number | Date | null): string {
  if (value == null || value === '') return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const seconds = Math.round((date.getTime() - Date.now()) / 1000);
  if (Math.abs(seconds) < 60) return 'just now';

  for (const [unit, size] of RELATIVE_UNITS) {
    if (Math.abs(seconds) >= size) {
      return relativeFormatter.format(Math.round(seconds / size), unit);
    }
  }
  return 'just now';
}

/** Zero-padded stat value ("7" → "07"), as the frontend's stat cards show it. */
export function pad2(value?: number | null): string {
  return String(value ?? 0).padStart(2, '0');
}

// What each artboard feature flag puts on a screen, in the words the frontend uses.
const SCREEN_FEATURE_LABELS: Record<string, string> = {
  Donation: 'Donations',
  Announcement: 'Announcements',
  ShiureiTorah: 'Shiurei Torah',
  MinyanRoll: 'Minyan roll',
  Zmanim_N: 'Zmanim board',
  Yurtzeiten_N: 'Yurtzeiten',
  RecentPhoto: 'Photos',
  Event: 'Events',
  Poster: 'Posters',
  DateTimeBanner: 'Date & time banner',
  WeeklyEvent: 'Weekly events',
  WeeklySchedule_N: 'Weekly schedule',
  PhotoUnderline: 'Photo caption',
  UpcomingMinyanim: 'Upcoming minyan',
};

export function screenFeatureLabel(feature: string): string {
  return SCREEN_FEATURE_LABELS[feature] || String(feature).replace(/_/g, ' ');
}

/** An artboard's features, whether stored as an array or a comma-separated string. */
export function artboardFeatures(features?: string[] | string | null): string[] {
  if (Array.isArray(features)) return features.filter(Boolean);
  if (typeof features === 'string' && features.length > 0) return features.split(',').filter(Boolean);
  return [];
}
